import React from "react";
import { Link } from "react-router-dom";
import { LegalPage, LegalSection, LegalList } from "@/components/site/LegalPage";
import { useSiteInfo, openCookiePreferences } from "@/lib/siteInfo";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

const Row = ({ name, purpose, duration, type }) => (
  <tr className="border-b border-white/5 align-top">
    <td className="py-3 pr-4 font-mono text-[11px] text-copperlight whitespace-nowrap">{name}</td>
    <td className="py-3 pr-4">{purpose}</td>
    <td className="py-3 pr-4 whitespace-nowrap">{duration}</td>
    <td className="py-3 whitespace-nowrap text-sand">{type}</td>
  </tr>
);

export default function CookiePolicy() {
  const { t } = useLang();
  useSeo({ title: t("seo.cookie.title"), description: t("seo.cookie.desc"), path: "/cookie-policy" });
  const info = useSiteInfo() || {};
  const owner = info.company_name || "[RAGIONE SOCIALE]";
  const email = info.privacy_email || "[EMAIL PRIVACY]";

  return (
    <LegalPage
      eyebrow="Informativa"
      title="Cookie Policy"
      updated={info.policy_updated_at}
      testId="cookie-policy-page"
    >
      <LegalSection title="1. Cosa sono i cookie">
        <p>
          I cookie sono piccoli file di testo che i siti salvano sul dispositivo di chi li visita per farli funzionare
          correttamente, ricordare le preferenze o raccogliere statistiche. Insieme ai cookie vengono qui considerati anche
          gli strumenti analoghi come il local storage del browser.
        </p>
        <p>Il titolare del trattamento è <strong className="text-cream">{owner}</strong> ({email}).</p>
      </LegalSection>

      <LegalSection title="2. Cookie tecnici (sempre attivi)">
        <p>
          Sono necessari al funzionamento del sito e, ai sensi dell'art. 122 del Codice Privacy e delle Linee guida del
          Garante, non richiedono il tuo consenso. Senza di essi il sito o l'area riservata non funzionano.
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm text-cream2" data-testid="cookie-table-technical">
            <thead>
              <tr className="border-b border-copper/30 text-left">
                <th className="py-2 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Nome</th>
                <th className="py-2 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Finalità</th>
                <th className="py-2 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Durata</th>
                <th className="py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">Tipo</th>
              </tr>
            </thead>
            <tbody>
              <Row name="access_token" purpose="Mantiene attiva la sessione di chi accede all'area riservata" duration="15 minuti" type="Prima parte" />
              <Row name="refresh_token" purpose="Rinnova la sessione dell'area riservata senza rifare l'accesso" duration="7 giorni" type="Prima parte" />
              <Row name="dlc_cookie_consent" purpose="Ricorda la scelta espressa su questo banner (local storage)" duration="Fino a cancellazione" type="Prima parte" />
              <Row name="Lingua nell'indirizzo" purpose="La lingua è indicata nell'URL (/en, /es): non usiamo cookie per memorizzarla" duration="Nessuna memorizzazione" type="Nessun cookie" />
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title="3. Cookie di statistica e di terze parti">
        {info.other_cookies ? (
          <>
            <p>
              Su questo sito sono attivi strumenti di misurazione che installano cookie solo dopo il tuo consenso,
              raccolto tramite il banner mostrato alla prima visita:
            </p>
            <LegalList
              items={[
                "Google Analytics (Google Ireland Ltd.): statistiche aggregate sulle pagine visitate, con indirizzo IP anonimizzato. Durata fino a 14 mesi. Informativa: policies.google.com/privacy",
                "PostHog: analisi dell'utilizzo del sito per migliorare pagine e percorsi di navigazione. Durata fino a 12 mesi. Informativa: posthog.com/privacy",
              ]}
            />
            <p>
              Se rifiuti, questi strumenti non vengono caricati e nessun cookie di statistica viene installato. Puoi
              cambiare idea in ogni momento:{" "}
              <button type="button" onClick={openCookiePreferences} className="link-underline text-copperlight" data-testid="cookie-policy-preferences-btn">
                rivedi le tue preferenze
              </button>
              .
            </p>
          </>
        ) : (
          <>
            <p>
              Al momento questo sito <strong className="text-cream">non utilizza</strong> cookie di statistica, di
              profilazione o di terze parti: nessun Google Analytics, nessun pixel pubblicitario, nessuna condivisione dei
              tuoi dati di navigazione con circuiti di marketing. Per questo non viene mostrato alcun banner di consenso.
            </p>
            <p>
              Se in futuro attiveremo strumenti di misurazione (Google Analytics, PostHog o simili) comparirà un banner che
              ti chiederà il consenso prima di installarli e questa pagina verrà aggiornata con l'elenco completo.
            </p>
          </>
        )}
      </LegalSection>

      <LegalSection title="4. Contenuti incorporati da altri servizi">
        <p>
          Alcune pagine possono incorporare video ospitati su piattaforme esterne (ad esempio YouTube o Vimeo). Quando
          avvii la riproduzione, la piattaforma può installare propri cookie: il trattamento è regolato dalle informative
          di quei servizi. Se non avvii la riproduzione, nessun cookie di terze parti viene installato.
        </p>
      </LegalSection>

      <LegalSection title="5. Come gestire i cookie dal browser">
        <p>
          Puoi bloccare o cancellare i cookie dalle impostazioni del tuo browser (Chrome, Safari, Firefox, Edge:
          sezione Privacy e sicurezza). Attenzione: disattivando i cookie tecnici l'area riservata non potrà funzionare.
        </p>
      </LegalSection>

      <LegalSection title="6. Altri riferimenti">
        <p>
          Per sapere come trattiamo i tuoi dati personali leggi la{" "}
          <Link to="/privacy" className="link-underline text-copperlight" data-testid="cookie-privacy-link">Privacy Policy</Link>.
          Per qualsiasi domanda scrivi a{" "}
          <a href={`mailto:${email}`} className="link-underline text-copperlight">{email}</a>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
